/**
 * Base API Client for RWA Console
 *
 * This module provides a configured fetch-based API client with:
 * - Automatic authentication header injection
 * - Request/response interceptors
 * - Typed error handling
 * - Session expiry detection and automatic logout
 * - Network error handling with retry support
 *
 * Usage:
 * ```typescript
 * import { apiClient } from '@/api/client';
 *
 * // GET request
 * const assets = await apiClient.get<Asset[]>('/assets');
 *
 * // POST request
 * const newAsset = await apiClient.post<Asset>('/assets', { name: 'New Asset' });
 * ```
 */

import { useAuthStore } from '@/stores/authStore';

// ============================================================================
// Configuration
// ============================================================================

/**
 * API base URL from environment variables
 * Falls back to localhost for development
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Default timeout for API requests in milliseconds
 */
const DEFAULT_TIMEOUT = 30000;

// ============================================================================
// Error Types
// ============================================================================

/**
 * Standard API error response structure from backend
 */
export interface ApiErrorResponse {
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Additional error details */
  details?: Record<string, unknown>;
  /** Validation errors for form fields */
  validationErrors?: Record<string, string[]>;
}

/**
 * Error codes returned by the API
 */
export const API_ERROR_CODES = {
  /** Authentication required - user not logged in */
  UNAUTHORIZED: 'UNAUTHORIZED',
  /** User doesn't have permission for this action */
  FORBIDDEN: 'FORBIDDEN',
  /** Resource not found */
  NOT_FOUND: 'NOT_FOUND',
  /** Validation error in request data */
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** Conflict with existing resource */
  CONFLICT: 'CONFLICT',
  /** Too many requests - rate limited */
  RATE_LIMITED: 'RATE_LIMITED',
  /** Server-side error */
  SERVER_ERROR: 'SERVER_ERROR',
  /** Network error - no connection */
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** Request timed out */
  TIMEOUT: 'TIMEOUT',
  /** Unknown error */
  UNKNOWN: 'UNKNOWN',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

/**
 * Custom error class for API errors with typed properties
 */
export class ApiError extends Error {
  /** HTTP status code */
  readonly status: number;
  /** API error code for programmatic handling */
  readonly code: ApiErrorCode;
  /** Additional error details from the API */
  readonly details?: Record<string, unknown>;
  /** Validation errors for form fields */
  readonly validationErrors?: Record<string, string[]>;
  /** Original response for debugging */
  readonly response?: Response;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCode,
    options?: {
      details?: Record<string, unknown>;
      validationErrors?: Record<string, string[]>;
      response?: Response;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = options?.details;
    this.validationErrors = options?.validationErrors;
    this.response = options?.response;
  }

  /**
   * Check if this is an authentication error
   */
  isAuthError(): boolean {
    return this.code === API_ERROR_CODES.UNAUTHORIZED;
  }

  /**
   * Check if this is a permission error
   */
  isPermissionError(): boolean {
    return this.code === API_ERROR_CODES.FORBIDDEN;
  }

  /**
   * Check if this is a validation error
   */
  isValidationError(): boolean {
    return this.code === API_ERROR_CODES.VALIDATION_ERROR;
  }

  /**
   * Check if this is a network error (no connection)
   */
  isNetworkError(): boolean {
    return this.code === API_ERROR_CODES.NETWORK_ERROR;
  }

  /**
   * Get validation error for a specific field
   */
  getFieldError(field: string): string | undefined {
    return this.validationErrors?.[field]?.[0];
  }
}

// ============================================================================
// Request Configuration
// ============================================================================

/**
 * Request options for API calls
 */
export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** Request body (will be JSON stringified) */
  body?: unknown;
  /** Custom headers to merge with defaults */
  headers?: HeadersInit;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Skip authentication header */
  skipAuth?: boolean;
  /** Custom query parameters */
  params?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Build URL with query parameters
 */
function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined | null>
): string {
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const fullPath = `${base}${normalizedPath}`;
  const url = fullPath.startsWith('http') ? new URL(fullPath) : new URL(fullPath, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Get authentication token from auth store
 * This is called outside of React component context
 */
function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

/**
 * Handle session expiry by logging out and redirecting
 */
function handleSessionExpiry(): void {
  const authStore = useAuthStore.getState();

  // Clear auth state
  authStore.logout();

  // Store current path for redirect after login
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath !== '/login') {
    sessionStorage.setItem('redirectAfterLogin', currentPath);
  }

  // Redirect to login page
  window.location.href = '/login';
}

/**
 * Map HTTP status code to API error code
 */
function mapStatusToErrorCode(status: number): ApiErrorCode {
  switch (status) {
    case 401:
      return API_ERROR_CODES.UNAUTHORIZED;
    case 403:
      return API_ERROR_CODES.FORBIDDEN;
    case 404:
      return API_ERROR_CODES.NOT_FOUND;
    case 409:
      return API_ERROR_CODES.CONFLICT;
    case 422:
      return API_ERROR_CODES.VALIDATION_ERROR;
    case 429:
      return API_ERROR_CODES.RATE_LIMITED;
    default:
      return status >= 500 ? API_ERROR_CODES.SERVER_ERROR : API_ERROR_CODES.UNKNOWN;
  }
}

// ============================================================================
// Request Interceptors
// ============================================================================

/**
 * Request interceptor type
 */
type RequestInterceptor = (
  url: string,
  options: ApiRequestOptions
) => Promise<{ url: string; options: ApiRequestOptions }> | { url: string; options: ApiRequestOptions };

/**
 * Response interceptor type
 */
type ResponseInterceptor = (
  response: Response,
  url: string,
  options: ApiRequestOptions
) => Promise<Response> | Response;

/**
 * Error interceptor type
 */
type ErrorInterceptor = (
  error: ApiError,
  url: string,
  options: ApiRequestOptions
) => Promise<ApiError | Response> | ApiError | Response;

/**
 * Registered request interceptors
 */
const requestInterceptors: RequestInterceptor[] = [];

/**
 * Registered response interceptors
 */
const responseInterceptors: ResponseInterceptor[] = [];

/**
 * Registered error interceptors
 */
const errorInterceptors: ErrorInterceptor[] = [];

/**
 * Add a request interceptor
 * @returns Function to remove the interceptor
 */
export function addRequestInterceptor(interceptor: RequestInterceptor): () => void {
  requestInterceptors.push(interceptor);
  return () => {
    const index = requestInterceptors.indexOf(interceptor);
    if (index !== -1) {
      requestInterceptors.splice(index, 1);
    }
  };
}

/**
 * Add a response interceptor
 * @returns Function to remove the interceptor
 */
export function addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
  responseInterceptors.push(interceptor);
  return () => {
    const index = responseInterceptors.indexOf(interceptor);
    if (index !== -1) {
      responseInterceptors.splice(index, 1);
    }
  };
}

/**
 * Add an error interceptor
 * @returns Function to remove the interceptor
 */
export function addErrorInterceptor(interceptor: ErrorInterceptor): () => void {
  errorInterceptors.push(interceptor);
  return () => {
    const index = errorInterceptors.indexOf(interceptor);
    if (index !== -1) {
      errorInterceptors.splice(index, 1);
    }
  };
}

// ============================================================================
// Core Request Function
// ============================================================================

/**
 * Execute API request with interceptors and error handling
 */
async function request<T>(
  method: string,
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  let url = buildUrl(path, options.params);
  let requestOptions = { ...options };

  // Run request interceptors
  for (const interceptor of requestInterceptors) {
    const result = await interceptor(url, requestOptions);
    url = result.url;
    requestOptions = result.options;
  }

  // Build headers
  const headers = new Headers(requestOptions.headers);

  // Set default content type for JSON
  if (!headers.has('Content-Type') && requestOptions.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  // Add Accept header
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  // Add authentication header
  if (!requestOptions.skipAuth) {
    const token = getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Build fetch options
  const fetchOptions: RequestInit = {
    method,
    headers,
    // Use default credentials (same-origin) since we use Bearer token auth
  };

  // Add body for non-GET requests
  if (requestOptions.body !== undefined && method !== 'GET') {
    fetchOptions.body = JSON.stringify(requestOptions.body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeout = requestOptions.timeout ?? DEFAULT_TIMEOUT;
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  fetchOptions.signal = controller.signal;

  let response: Response;

  try {
    response = await fetch(url, fetchOptions);
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle network errors and timeouts
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiError(
          'Request timed out',
          0,
          API_ERROR_CODES.TIMEOUT
        );
      }
      throw new ApiError(
        'Network error: Unable to connect to server',
        0,
        API_ERROR_CODES.NETWORK_ERROR,
        { details: { originalError: error.message } }
      );
    }
    throw new ApiError(
      'Unknown network error',
      0,
      API_ERROR_CODES.UNKNOWN
    );
  }

  clearTimeout(timeoutId);

  // Run response interceptors
  for (const interceptor of responseInterceptors) {
    response = await interceptor(response, url, requestOptions);
  }

  // Handle error responses
  if (!response.ok) {
    let errorData: ApiErrorResponse | undefined;

    try {
      errorData = await response.json();
    } catch {
      // Response body is not JSON
    }

    const errorCode = errorData?.code as ApiErrorCode ?? mapStatusToErrorCode(response.status);
    const errorMessage = errorData?.message ?? response.statusText ?? 'Request failed';

    const apiError = new ApiError(errorMessage, response.status, errorCode, {
      details: errorData?.details,
      validationErrors: errorData?.validationErrors,
      response,
    });

    // Run error interceptors
    for (const interceptor of errorInterceptors) {
      const result = await interceptor(apiError, url, requestOptions);
      if (result instanceof Response) {
        // Interceptor recovered from error
        response = result;
        break;
      }
    }

    // Handle session expiry (401)
    if (response.status === 401 && !requestOptions.skipAuth) {
      handleSessionExpiry();
    }

    throw apiError;
  }

  // Parse response
  if (response.status === 204) {
    // No content
    return undefined as T;
  }

  // Check if response is JSON
  const contentType = response.headers.get('Content-Type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }

  // Return text for non-JSON responses
  return response.text() as unknown as T;
}

// ============================================================================
// HTTP Method Helpers
// ============================================================================

/**
 * API client with typed methods for each HTTP verb
 */
export const apiClient = {
  /**
   * Perform GET request
   */
  get<T>(path: string, options?: Omit<ApiRequestOptions, 'body'>): Promise<T> {
    return request<T>('GET', path, options);
  },

  /**
   * Perform POST request
   */
  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>('POST', path, { ...options, body });
  },

  /**
   * Perform PUT request
   */
  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>('PUT', path, { ...options, body });
  },

  /**
   * Perform PATCH request
   */
  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>('PATCH', path, { ...options, body });
  },

  /**
   * Perform DELETE request
   */
  delete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return request<T>('DELETE', path, options);
  },

  /**
   * Upload file(s) with multipart/form-data
   * Note: Don't set Content-Type header - browser will set it with boundary
   */
  async upload<T>(
    path: string,
    formData: FormData,
    options?: Omit<ApiRequestOptions, 'body'>
  ): Promise<T> {
    const url = buildUrl(path, options?.params);
    const headers = new Headers(options?.headers);

    // Add authentication header
    if (!options?.skipAuth) {
      const token = getAuthToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    // Add Accept header
    headers.set('Accept', 'application/json');

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      let errorData: ApiErrorResponse | undefined;

      try {
        errorData = await response.json();
      } catch {
        // Response body is not JSON
      }

      const errorCode = errorData?.code as ApiErrorCode ?? mapStatusToErrorCode(response.status);
      const errorMessage = errorData?.message ?? response.statusText ?? 'Upload failed';

      throw new ApiError(errorMessage, response.status, errorCode, {
        details: errorData?.details,
        response,
      });
    }

    return response.json();
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extract user-friendly error message from any error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Format validation errors from API response for form display
 */
export function formatValidationErrors(
  error: ApiError
): Record<string, string> {
  if (!error.validationErrors) {
    return {};
  }

  const formatted: Record<string, string> = {};
  for (const [field, messages] of Object.entries(error.validationErrors)) {
    formatted[field] = messages[0] ?? 'Invalid value';
  }
  return formatted;
}

// ============================================================================
// Default Interceptors
// ============================================================================

// Add default error interceptor for logging in development
if (import.meta.env.DEV) {
  addErrorInterceptor((error, url) => {
    // Log errors in development for debugging
    if (error.code !== API_ERROR_CODES.UNAUTHORIZED) {
      // eslint-disable-next-line no-console
      console.error(`[API Error] ${error.code} - ${url}:`, {
        status: error.status,
        message: error.message,
        details: error.details,
      });
    }
    return error;
  });
}

export default apiClient;
